/**
 * WebSocket connection singleton.
 *
 * Manages the organization WebSocket lifecycle independently of the React
 * component tree. This means AuthWrapper can unmount/remount RouterProvider
 * freely without killing the WebSocket.
 *
 * Core responsibilities (always active):
 * - Connection lifecycle (connect, reconnect via ws.ts)
 * - Applying incoming messages to the Zustand store
 * - Notifying the dispatcher when snapshot completes
 * - Tracking connection state in the store
 *
 * React integration (active only when RootComponent is mounted):
 * - Extra message handling (onboarding navigation, error state)
 * - Auth-required redirect
 */

import { connectWS, type WsConnection, type TokenGetter } from './ws';
import { notifyDispatcherReady } from './messaging';
import { Kind } from '@interlucent/organization-protocol';
import { useStore } from './store';
import { logger } from './log';
import type { WsConnectionState } from './ws';

const log = logger('ws-connection');

// ---------------------------------------------------------------------------
// Singleton state
// ---------------------------------------------------------------------------

let connection: WsConnection | null = null;
let currentOrgId: string | null = null;
let currentWs: WebSocket | null = null;

// ---------------------------------------------------------------------------
// React-layer callback refs (set/cleared by RootComponent)
// ---------------------------------------------------------------------------

let onAuthRequiredCb: (() => void) | null = null;
let extraMessageHandler: ((msg: any) => void) | null = null;

// ---------------------------------------------------------------------------
// Message handler — always runs, even if React is unmounted
// ---------------------------------------------------------------------------

function handleMessage(msg: any): void {
  log.debug(`Received message, kind: ${msg.kind}, type: ${msg.type}`);

  // Always apply to the Zustand store (module-level, survives unmounts)
  useStore.getState().apply(msg);

  // Notify dispatcher when snapshot completes
  if (msg.kind === Kind.SnapshotEnd) {
    notifyDispatcherReady();
  }

  // Delegate to React-layer handler if registered
  extraMessageHandler?.(msg);
}

// ---------------------------------------------------------------------------
// Connection management
// ---------------------------------------------------------------------------

/**
 * Initialize (or re-use) the WebSocket connection for a given org.
 * Idempotent — calling with the same orgId while connected is a no-op.
 * Calling with a different orgId disposes the old connection first.
 */
export function initOrgConnection(
  orgId: string,
  tokenGetter: TokenGetter,
): void {
  log.info(`initOrgConnection called: orgId=${orgId}, currentOrgId=${currentOrgId}, hasConnection=${!!connection}`);

  // Same org — already connected or connecting
  if (currentOrgId === orgId) {
    log.debug('Already connected/connecting to this org, skipping');
    return;
  }

  // Different org — dispose old connection
  if (connection) {
    log.info('Org changed, disposing old connection');
    connection.dispose();
    connection = null;
    currentWs = null;
  }

  currentOrgId = orgId;
  log.info(`Starting connectWS for org=${orgId}`);

  connectWS(tokenGetter, handleMessage, true, 0, {
    onStateChange: (state: WsConnectionState) => {
      log.info(`onStateChange: ${state} (currentOrgId=${currentOrgId}, orgId=${orgId})`);
      useStore.setState((prev) => ({
        connectionState: state,
        wsEverConnected: prev.wsEverConnected || state === 'connected',
      }));
    },
    onNewSocket: (ws: WebSocket) => {
      log.info(`onNewSocket for org=${orgId}`);
      currentWs = ws;
    },
    onAuthRequired: () => {
      onAuthRequiredCb?.();
    },
  }).then((conn) => {
    log.info(`connectWS resolved for org=${orgId}, setting connection (ws=${conn.ws ? 'present' : 'null'})`);
    connection = conn;
  });
}

/**
 * Tear down the connection entirely.
 * Only call on explicit org change or sign-out — NOT on component unmount.
 */
export function disposeOrgConnection(): void {
  log.info(`disposeOrgConnection called: hasConnection=${!!connection}, currentOrgId=${currentOrgId}`);
  if (connection) {
    connection.dispose();
    connection = null;
    currentOrgId = null;
    currentWs = null;
    useStore.setState({ connectionState: 'disconnected' });
  }
}

/** Get the current WebSocket (may be null during connect/reconnect). */
export function getWs(): WebSocket | null {
  return currentWs;
}

// ---------------------------------------------------------------------------
// React integration — called by RootComponent
// ---------------------------------------------------------------------------

export interface ConnectionReactCallbacks {
  onAuthRequired?: () => void;
  /** Handle messages that need React context (navigation, error state). */
  extraMessageHandler?: (msg: any) => void;
}

/**
 * Register React-layer callbacks. Call from a useEffect in RootComponent.
 * These are called in addition to the core message handling (store + dispatcher).
 */
export function setConnectionCallbacks(callbacks: ConnectionReactCallbacks): void {
  onAuthRequiredCb = callbacks.onAuthRequired ?? null;
  extraMessageHandler = callbacks.extraMessageHandler ?? null;
}

/** Clear all React-layer callbacks. Call in the useEffect cleanup. */
export function clearConnectionCallbacks(): void {
  onAuthRequiredCb = null;
  extraMessageHandler = null;
}
